/*
 *  saga to load auth user details from the just token
 */

import { forEach, uniq } from 'lodash'
import {
  delay,
  put,
  takeEvery,
  takeLatest,
  select,
  call,
} from 'redux-saga/effects'

import {
  createDashboardCompetitorAnalysisReport,
  createProject,
  getCompetitorAnalysisReport,
  getProjects,
  queryDomainOverview,
  updateProject,
  deleteProject,
  getProject,
  getLocationsData,
  saveSearchHistory,
  generateLeads,
  DEFAULT_NUMBER_OF_API_CALL_RETRIES,
} from '../../utils/api'
import { getDomain } from '../../utils/functions'
import history from '../../history'
import { getParentLocId, isCity } from '../../utils/locations'
import { REPORT_POLL_DELAY } from '../../utils/consts.mjs'
import { isSignedIn } from '../../utils/userAuth'
import {
  deleteGoogleStatus,
  getRecentProject,
  removeRecentProject,
  saveNewProjectStatus,
  saveRecentProject,
} from '../../utils/storage'
import { fetchUser } from '../../state/actions/global'
import { getLocationsInfo } from '../../state/actions/locationManager'
import { mapAlertsFromProjects } from '../../state/sagas/alerts.saga'
import { fetchAlertsSuccess } from '../../state/actions/alerts'
import { selectAlerts } from '../../state/selectors/alerts'
import { selectCachedLocations } from '../../state/selectors/locationManager'
import { Mixpanel, MIXPANEL_PROPS } from '../../utils/mixpanel'

import {
  selectCompetitorTrackingLocation,
  selectCurrentProject,
  selectProjects,
} from './selectors'
import {
  addProjectFailed,
  addProjectSuccess,
  addSearchHistoryFailed,
  addSearchHistorySuccess,
  ADD_COMPETITORS,
  ADD_COMPETITOR_LOCATION,
  ADD_PROJECT,
  ADD_SEARCH_HISTORY,
  changeCompetitorTrackingLocation,
  CHANGE_COMPETITOR_TRACKING_LOCATION,
  CHANGE_CURRENT_PROJECT,
  deleteProjectFailed,
  deleteProjectSuccess,
  DELETE_COMPETITORS,
  DELETE_COMPETITOR_LOCATION,
  DELETE_PROJECT,
  fetchProjectsSuccess,
  FETCH_PROJECTS,
  getDashboardCompetitorAnalysisReportFailed,
  getDashboardCompetitorAnalysisReportPolling,
  getDashboardCompetitorAnalysisReportSuccess,
  GET_DASHBOARD_COMPETITOR_ANALYSIS_REPORT,
  reloadProject,
  reloadProjectSuccess,
  RELOAD_PROJECT,
  updateProjectFailed,
  updateProjectSuccess,
  UPDATE_PROJECT,
} from './actions'

export default function* dashboardSagas() {
  yield takeLatest(FETCH_PROJECTS.action, (action) =>
    fetchProjectsHandler(action.payload),
  )

  yield takeEvery(CHANGE_COMPETITOR_TRACKING_LOCATION.action, (action) =>
    changeCompetitorTrackingLocationHandler(action.payload),
  )

  yield takeEvery(GET_DASHBOARD_COMPETITOR_ANALYSIS_REPORT.action, (action) =>
    getDashboardCompetitorsHandler(action.payload),
  )

  yield takeEvery(CHANGE_CURRENT_PROJECT.action, (action) =>
    changeCurrentProjectHandler(action.projectId),
  )

  yield takeEvery(RELOAD_PROJECT.action, (action) =>
    reloadProjectHandler(action.projectId, action.onComplete),
  )

  yield takeEvery(ADD_PROJECT.action, (action) =>
    addProjectHandler(action.payload, action.leadsData, action.onComplete),
  )

  yield takeEvery(DELETE_PROJECT.action, (action) =>
    deleteProjectHandler(action.onComplete, action.projectId),
  )

  yield takeEvery(UPDATE_PROJECT.action, (action) =>
    updateProjectHandler(action.id, action.payload, action.onComplete),
  )

  yield takeEvery(ADD_COMPETITORS.action, updateCompetitorsHandler)

  yield takeEvery(DELETE_COMPETITORS.action, updateCompetitorsHandler)

  yield takeEvery(ADD_COMPETITOR_LOCATION.action, updateCompetitorsHandler)

  yield takeEvery(DELETE_COMPETITOR_LOCATION.action, updateCompetitorsHandler)

  yield takeEvery(ADD_SEARCH_HISTORY.action, (action) =>
    addSearchHistoryHandler(action.searchType, action.searchParams),
  )
}

function* fetchProjectsHandler(activeProjectId) {
  const { HAS_PROJECT, HAS_PROJECT_WITH_TRACKED_KEYWORDS } = MIXPANEL_PROPS
  try {
    if (!isSignedIn()) {
      yield put(
        fetchProjectsSuccess({
          projects: [],
          currentProject: null,
          isLoadingProjects: false,
        }),
      )
    } else {
      const projectsResponse = yield getProjects()
      const projects = projectsResponse.data.projects || []

      const savedProject = getRecentProject(projects[0])

      const currentProject =
        projects.find(
          (project) => project.id === (activeProjectId || savedProject.id),
        ) || projects[0]

      const hasProjectWithTrackedKeywords = projects.some(
        (project) => !!Object.keys(project?.keywords || {}).length,
      )

      Mixpanel.registerAndSetPeople({
        [HAS_PROJECT.name]: !!currentProject,
        [HAS_PROJECT_WITH_TRACKED_KEYWORDS.name]: hasProjectWithTrackedKeywords,
      })

      yield put(
        fetchProjectsSuccess({
          projects,
          currentProject,
          isLoadingProjects: false,
        }),
      )
      if (projects && projects.length > 0) {
        const cachedLocations = yield select(selectCachedLocations())
        const locIds = []
        forEach(projects, (project) => {
          locIds.push(
            ...project.locations
              .map((l) => l.loc_id)
              .filter((locId) => isCity(locId) && !cachedLocations[locId]),
          )
        })
        if (locIds.length > 0) yield put(getLocationsInfo(uniq(locIds)))
      }
    }
  } catch (e) {
    console.error(e)
  }
}

function* changeCompetitorTrackingLocationHandler({ locId, lang }) {
  try {
    const currentProject = yield select(selectCurrentProject())
    let domains
    if (currentProject.competitors) {
      domains = []
      forEach(currentProject.competitors, (locations, key) => {
        if (
          locations.some(
            (l) => getParentLocId(l.loc_id) === locId && l.lang === lang,
          )
        )
          domains.push(key)
      })
    }

    yield getDashboardCompetitorsHandler({
      projectId: currentProject.id,
      domain: currentProject.domain,
      locId,
      lang,
      domains,
      includeSuggested: true,
      includeDomainOverview: true,
    })
  } catch (e) {
    console.error(e)
  }
}

function* getDashboardCompetitorsHandler(payload) {
  let domainOverviewResponse
  const {
    projectId,
    domain,
    locId,
    lang,
    locations,
    domains,
    includeSuggested = true,
    limit,
    includeDomainOverview = true,
  } = payload
  const currentLocations =
    lang && locId ? { lang, loc_id: locId } : locations.shift()
  const { lang: language } = currentLocations
  let { loc_id: locationId } = currentLocations
  try {
    if (isCity(locationId)) {
      const res = yield getLocationsData([locationId], language)
      locationId = res?.data?.length > 0 ? res.data[0].parent_id : locationId
    }
    if (includeDomainOverview) {
      domainOverviewResponse = yield queryDomainOverview(
        getDomain(domain),
        locationId,
        language,
      )
    }
    const reportResponse = yield createDashboardCompetitorAnalysisReport(
      projectId,
      domain,
      locationId,
      language,
      domains,
      includeSuggested,
      limit,
    )
    if (reportResponse?.data?.reportId) {
      yield put(
        getDashboardCompetitorAnalysisReportPolling({
          reportId: reportResponse.data.reportId,
          competitors: reportResponse.data.competitors,
          domainData: domainOverviewResponse?.data,
          domains: domains
            ? domains.map((c, index) => ({
                id: index,
                url: c,
                checked: true,
              }))
            : [],
          competitorsData: {
            competitors: [],
            pendingData: true,
          },
        }),
      )
      yield delay(500)
      yield pollDashboardCompetitorsHandler(reportResponse.data.reportId)
    } else {
      throw new Error('No reportId')
    }
  } catch (e) {
    if (
      e?.response?.data?.code === 'wrong_loc_or_lang' &&
      locations.length > 0
    ) {
      yield call(getDashboardCompetitorsHandler, payload)
    } else {
      yield put(
        getDashboardCompetitorAnalysisReportFailed({
          competitors: [],
          domainData: domainOverviewResponse?.data || { noData: true },
          domains: domains
            ? domains.map((c, index) => ({
                id: index,
                url: c,
                checked: true,
              }))
            : [],
          competitorsData: {
            competitors: [],
            pendingData: false,
            error: e?.response?.data?.code,
          },
        }),
      )
      console.error(e)
    }
  }
}

function* pollDashboardCompetitorsHandler(
  reportId,
  retry = DEFAULT_NUMBER_OF_API_CALL_RETRIES,
) {
  try {
    const reportResponse = yield getCompetitorAnalysisReport(reportId)
    const { pendingData } = reportResponse.data

    if (pendingData) {
      yield put(
        getDashboardCompetitorAnalysisReportPolling({
          competitorsData: reportResponse.data,
        }),
      )
      yield delay(REPORT_POLL_DELAY)
      yield pollDashboardCompetitorsHandler(reportId)
    } else {
      yield put(
        getDashboardCompetitorAnalysisReportSuccess({
          competitorsData: reportResponse.data,
        }),
      )
    }
  } catch (error) {
    console.error(error)
    const previousTokenError =
      error.response &&
      error.response.status === 403 &&
      error.response.data &&
      error.response.data.error === 'FORBIDDEN' &&
      error.response.data.description.startsWith('The token')

    const remainingCount = parseInt(
      error.response.headers['x-ratelimit-remaining'] || '0',
      10,
    )
    if ((previousTokenError && remainingCount === 0) || retry === 0) {
      yield put(getDashboardCompetitorAnalysisReportFailed())
      return
    }
    yield delay(REPORT_POLL_DELAY)
    yield pollDashboardCompetitorsHandler(reportId, retry - 1)
  }
}

function* updateCompetitorsHandler({
  type,
  payload: { competitors, competitorsLocations, addLocation, deleteLocation },
  onComplete = () => ({}),
}) {
  try {
    const currentProject = yield select(selectCurrentProject())
    let { locId, lang } = yield select(selectCompetitorTrackingLocation())
    yield updateProjectHandler(currentProject.id, {
      competitors,
      competitorsLocations,
    })
    const newCurrentProject = yield select(selectCurrentProject())
    if (
      (type === DELETE_COMPETITORS.action && competitorsLocations) ||
      (type === DELETE_COMPETITOR_LOCATION.action &&
        locId === deleteLocation.loc_id &&
        lang === deleteLocation.lang)
    ) {
      if (newCurrentProject.competitors_locations.length > 0) {
        locId = newCurrentProject.competitors_locations[0].loc_id
        lang = newCurrentProject.competitors_locations[0].lang
      } else {
        history.push('/dashboard')
        return
      }
    } else if (type === ADD_COMPETITOR_LOCATION.action) {
      locId = addLocation.loc_id
      lang = addLocation.lang
    }

    yield put(changeCompetitorTrackingLocation({ locId, lang }))
  } catch (e) {
    console.error(e)
    onComplete(e)
  }
}

function* addProjectHandler(payload, leadsData, onComplete) {
  try {
    const { HAS_PROJECT, HAS_PROJECT_WITH_TRACKED_KEYWORDS } = MIXPANEL_PROPS
    const projects = yield select(selectProjects())
    const newProjectResponse = yield createProject(payload)
    const newProject = newProjectResponse.data.project

    if (!projects?.length) {
      Mixpanel.registerAndSetPeople({
        [HAS_PROJECT.name]: HAS_PROJECT.values.TRUE,
      })
    }

    if (Object.keys(payload.keywords).length) {
      Mixpanel.registerAndSetPeople({
        [HAS_PROJECT_WITH_TRACKED_KEYWORDS.name]:
          HAS_PROJECT_WITH_TRACKED_KEYWORDS.values.TRUE,
      })
    }

    if (leadsData) {
      const utmParameters = JSON.parse(sessionStorage.getItem('utm_parameters'))
      const externalReferrer = sessionStorage.getItem('external_referrer')
      const internalReferrer = window.location.href
      yield generateLeads(leadsData, {
        ...utmParameters,
        ref: internalReferrer,
        ref_ext: externalReferrer,
      })
    }
    saveRecentProject(newProject)
    saveNewProjectStatus(newProject.id, true)

    yield put(
      addProjectSuccess({
        projects: [...(projects || []), newProject],
        currentProject: newProject,
      }),
    )

    if (onComplete) {
      onComplete({ data: newProject })
    }
  } catch (e) {
    console.error(e)
    yield put(addProjectFailed(e))
    if (onComplete) {
      onComplete({ error: e })
    }
  }
}

function* deleteProjectHandler(onComplete, projectId = null) {
  try {
    const currentProject = yield select(selectCurrentProject())
    const project_id = projectId || currentProject.id
    yield deleteProject(project_id)
    const oldProjects = yield select(selectProjects())
    const newProjects = oldProjects.filter((p) => p.id !== project_id)

    removeRecentProject(project_id)
    deleteGoogleStatus(project_id)
    const [newCurrentProject] = newProjects
    if (newCurrentProject) {
      saveRecentProject(newCurrentProject)
    }

    yield put(
      deleteProjectSuccess({
        projects: newProjects,
        currentProject: newCurrentProject,
      }),
    )

    yield put(fetchUser())

    if (onComplete) {
      onComplete({ data: currentProject })
    }
  } catch (e) {
    console.error(e)
    yield put(deleteProjectFailed(e))
    if (onComplete) {
      onComplete({ error: e })
    }
  }
}

function* updateProjectHandler(id, payload, onComplete) {
  try {
    const { HAS_PROJECT_WITH_TRACKED_KEYWORDS } = MIXPANEL_PROPS
    const currentProject = yield select(selectCurrentProject())
    const oldProjects = yield select(selectProjects())

    const { locations, competitorsLocations } = payload

    const updatedLocations = locations
      ? locations.map((location) => ({
          ...location,
          loc_id: Number(location.loc_id),
        }))
      : undefined

    const updatedCompetitorsLocations = competitorsLocations
      ? competitorsLocations.map((location) => ({
          ...location,
          loc_id: Number(location.loc_id),
        }))
      : undefined

    const updatedPayload = {
      ...payload,
      ...(updatedLocations && { locations: updatedLocations }),
      ...(updatedCompetitorsLocations && {
        competitorsLocations: updatedCompetitorsLocations,
      }),
    }

    const updateProjectResponse = yield updateProject(id, updatedPayload)
    const updatedProject = updateProjectResponse.data.project

    const alerts = yield select(selectAlerts())
    const projects = oldProjects.map((project) =>
      project.id === updatedProject.id
        ? {
            ...project,
            ...updatedProject,
          }
        : project,
    )

    const hasProjectWithTrackedKeywords = projects.some(
      (project) => !!Object.keys(project?.keywords || {}).length,
    )

    Mixpanel.registerAndSetPeople({
      [HAS_PROJECT_WITH_TRACKED_KEYWORDS.name]: hasProjectWithTrackedKeywords,
    })

    yield put(
      updateProjectSuccess({
        projects,
        currentProject:
          id === currentProject.id
            ? {
                ...currentProject,
                ...updatedProject,
              }
            : currentProject,
      }),
    )
    if (onComplete) {
      onComplete({ data: updatedProject })
    }
    yield put(fetchAlertsSuccess(mapAlertsFromProjects(alerts, projects)))
  } catch (e) {
    console.error(e)
    yield put(updateProjectFailed(e))
    if (onComplete) {
      onComplete({ error: e })
    }
  }
}

function* reloadProjectHandler(id, onComplete) {
  const oldProjects = yield select(selectProjects())
  const currentProject = yield select(selectCurrentProject())

  try {
    // Update Project
    const projectResponse = yield getProject(id)
    const { project } = projectResponse.data

    yield put(
      reloadProjectSuccess({
        projects: oldProjects.map(
          (p) =>
            p.id === project.id
              ? {
                  ...p,
                  ...project,
                }
              : p, // replace current project with updated project
        ),
        currentProject:
          id === currentProject.id
            ? {
                ...currentProject,
                ...project,
              }
            : currentProject,
      }),
    )
    if (onComplete) onComplete({ data: project })
  } catch (e) {
    console.error(e)
    if (onComplete) onComplete({})
  }
}

function* changeCurrentProjectHandler(id) {
  const projects = yield select(selectProjects())
  const currentProject = projects.find((p) => p.id === id)
  if (currentProject?.keywords_updated_at === 0) yield put(reloadProject(id))
}

function* addSearchHistoryHandler(searchType, searchParams) {
  if (!isSignedIn()) return

  try {
    let currentProject = yield select(selectCurrentProject())
    let retry = 3
    while (true) {
      currentProject = yield select(selectCurrentProject())
      if (currentProject && currentProject.workspace?.id) {
        break
      } else if (retry > 0) {
        yield delay(2000)
        retry -= 1
      } else if (retry === 0) {
        break
      }
    }

    yield saveSearchHistory(currentProject?.workspace?.id, {
      search_type: searchType,
      search_params: searchParams,
    })

    yield put(addSearchHistorySuccess())
  } catch (e) {
    yield put(addSearchHistoryFailed())
  }
}
